import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import BreadcrumbService from '../../services/breadcrumbService';

const Breadcrumbs = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [participantDetails, setParticipantDetails] = useState(null);
    const [processedCrumbs, setProcessedCrumbs] = useState([]);
    
    useEffect(() => {
        const loadBreadcrumbData = async () => {
            try {
                // Force refresh to get the latest participant data
                const data = await BreadcrumbService.getBreadcrumbData(true);
                buildBreadcrumbs(data);
            } catch (error) {
                console.error('Failed to load breadcrumb data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadBreadcrumbData();
    }, [location]);

    const segmentsToHide = [
      'participant',
      'participants',
      'studies'
    ];

    const segmentDict = {
      'studies': 'Studies',
      'studybuilder': 'Study Builder',
      'drafts': 'Study Drafts',
      'library': 'Library',
      'schedule': 'Schedule',
      'profile': 'Profile',
      'compliance': 'Compliance',
      'edit': 'Edit',
      'file-upload': 'File Upload',
      'sites': 'Sites',
      'participant': 'Participant',
      'add-participant': 'Add Participant',
      'dcq': 'DCQ',
      'create': 'Create',
      'create_dcq': 'Create DCQ',
      'add-site': 'Add Site',
      'upload': 'Upload',
      'global-library': 'Global Library'
    };

    const buildBreadcrumbs = (breadcrumbData) => {
      let pathName =  location.pathname;
      let pathnames = pathName.split('/').filter((x) => x);;
      // Process the path segments
      let crumbs = [];
      let currentPath = '';


      crumbs.push({
        name: 'Home',
        path: '/',
        isClickable: true
      });

      pathnames.forEach((segment, index) => {
        currentPath += '/' + segment;        
        const prevName = index > 0 ? pathnames[index - 1] : '';
    
        if (segmentsToHide.includes(segment)) {
          // ignore segment
        }
        else if (segmentDict[segment] != null) {
          crumbs.push({
            name: segmentDict[segment],
            path: currentPath,
            isClickable: true
          });
        }
        else if (prevName === 'study' || prevName === 'studies') {
          crumbs.push({
            name: decodeSegment(breadcrumbData.studies[segment] ? breadcrumbData.studies[segment] : segment),
            path: currentPath,
            isClickable: true
          });
        }
        else if (prevName === 'site' || prevName === 'sites') {
          crumbs.push({
            name: decodeSegment(breadcrumbData.sites[segment] ? breadcrumbData.sites[segment] : segment),
            path: currentPath,
            isClickable: true
          });
        }
        else if (prevName === 'participant' || prevName === 'participants') {
          crumbs.push({
            name: decodeSegment(breadcrumbData.participants[segment] ? breadcrumbData.participants[segment] : segment),
            path: currentPath,
            isClickable: true
          });
        }
        else if (prevName == 'completed-surveys') {

        }
        else {
          crumbs.push({
            name: decodeSegment(segment),
            path: currentPath,
            isClickable: true
          });
        }
    
        // show the 'tab' if the path ends with /sites or /dcq
        let last = crumbs[crumbs.length - 1];
        last.path = last.path.replace(/\/sites$/, '#sites');
        last.path = last.path.replace(/\/dcq$/, '#dcq');
      });

      setProcessedCrumbs(crumbs);
    };

    const decodeSegment = (segment) => {
      try {
          return decodeURIComponent(segment);
      } catch (error) {
          console.error('Error decoding segment:', segment, error);
          return segment;
      }
    };

    const handleClick = (e, segment) => {
        e.preventDefault();
        if (segment.path.includes('?tab=')) {
            const [path, query] = segment.path.split('?');
            navigate(path);
        } else {
            navigate(segment.path);
        }
    };

    return (
        <nav className="text-sm" aria-label="Breadcrumb">
            <ol className="list-none p-0 inline-flex">
                {processedCrumbs.map((segment, index) => {
                    const isLast = index === processedCrumbs.length - 1;
                    const isClickable = segment.isClickable && !isLast;
                    
                    return (
                        <li key={segment.path} className="flex items-center">
                            {index > 0 && (
                                <ChevronRight className="flex-shrink-0 h-4 w-4 text-gray-400 mx-2" />
                            )}
                            {isClickable ? (
                                <Link
                                    to={segment.path}
                                    onClick={(e) => handleClick(e, segment)}
                                    className="text-primary-600 hover:text-primary-800 transition duration-150 ease-in-out"
                                >
                                    {segment.name}
                                </Link>
                            ) : (
                                <span className="text-gray-700" aria-current={isLast ? "page" : undefined}>
                                    {segment.name}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Breadcrumbs;